import { useState } from 'react'
import {
  PALETTE_KEYS,
  isLightTheme,
  CUSTOM_THEME,
  accentVarsFor,
  themeVars,
  type PaletteRecord,
} from '@/lib/colour'
import { Button, Sheet, cx } from '@/components/ui'

/**
 * Hand-tune a workspace's palette: every layer of the surface plus the accent,
 * each as a colour well (hex) or any CSS colour typed in, with a live preview
 * of the shell drawn from the draft. Nothing is applied until "Use this".
 */
export const ThemeEditor = ({
  value,
  onUse,
  onClose,
}: {
  value: PaletteRecord
  onUse: (palette: PaletteRecord) => void
  onClose: () => void
}) => {
  const [draft, setDraft] = useState<PaletteRecord>(value)
  const set = (key: string, v: string) => setDraft((d) => ({ ...d, [key]: v }))
  const light = draft.scheme === 'light'
  return (
    <Sheet
      open
      onClose={onClose}
      title="Edit palette"
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={() => onUse(draft)} data-testid="palette-use">
            Use this
          </Button>
        </>
      }
    >
      <Preview palette={draft} />
      <div className="flex gap-1 mb-3" role="radiogroup" aria-label="Scheme">
        {(['dark', 'light'] as const).map((s) => (
          <button
            key={s}
            type="button"
            role="radio"
            aria-checked={draft.scheme === s}
            onClick={() => set('scheme', s)}
            className={cx(
              'rounded-full px-3 min-h-8 text-xs border',
              draft.scheme === s ? 'border-accent bg-accent-soft text-fg' : 'border-line text-muted',
            )}
            data-testid={`palette-scheme-${s}`}
          >
            {s === 'dark' ? 'Dark' : 'Light'}
          </button>
        ))}
        <span className="ml-auto self-center text-[11px] text-faint">
          {light ? 'text on the accent goes dark' : 'controls and system UI go dark'}
        </span>
      </div>
      <div className="flex flex-col gap-1.5">
        {PALETTE_KEYS.map(([key, label]) => (
          <ColourRow
            key={key}
            label={label}
            value={draft[key] ?? ''}
            onChange={(v) => set(key, v)}
            id={key}
          />
        ))}
      </div>
    </Sheet>
  )
}

const isHex = (v: string) => /^#[0-9a-f]{6}$/i.test(v.trim())

const ColourRow = ({
  id,
  label,
  value,
  onChange,
}: {
  id: string
  label: string
  value: string
  onChange: (v: string) => void
}) => (
  <label className="flex items-center gap-2 min-h-10">
    <span
      className="relative h-8 w-8 shrink-0 rounded-lg border border-line overflow-hidden"
      style={{ background: value }}
    >
      {/* the native well only speaks hex; anything else is edited in the text field */}
      <input
        type="color"
        value={isHex(value) ? value.trim().toLowerCase() : '#000000'}
        onChange={(e) => onChange(e.target.value)}
        className="absolute inset-0 h-full w-full opacity-0 cursor-pointer"
        aria-label={`${label} colour`}
        data-testid={`palette-well-${id}`}
      />
    </span>
    <span className="w-28 text-sm">{label}</span>
    <input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="flex-1 min-w-0 font-mono text-xs"
      spellCheck={false}
      autoCapitalize="off"
      data-testid={`palette-${id}`}
    />
  </label>
)

/** A corner of the shell drawn from the draft: enough to judge contrast and mood. */
const Preview = ({ palette }: { palette: PaletteRecord }) => {
  const vars = {
    ...themeVars(CUSTOM_THEME, palette),
    ...accentVarsFor(palette, undefined, isLightTheme(CUSTOM_THEME, palette)),
  } as React.CSSProperties
  return (
    <div
      className="rounded-2xl border border-line p-3 mb-3 bg-ink text-fg"
      style={vars}
      data-testid="palette-preview"
    >
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-semibold">Today</span>
        <span className="text-[11px] text-faint">Sun 7 Sep</span>
      </div>
      <div className="rounded-xl bg-panel border border-line p-2.5 mb-2">
        <div className="text-sm">Book the dentist</div>
        <div className="text-[11px] text-muted mt-0.5">
          15 min · <span className="text-accent-text">home</span>
        </div>
      </div>
      <div className="rounded-xl bg-raised border border-line-strong p-2.5 mb-2">
        <div className="text-sm text-fg-2">Draft the talk outline</div>
        <div className="text-[11px] text-faint mt-0.5">Wednesday</div>
      </div>
      <div className="flex gap-2">
        <span className="rounded-full bg-accent text-on-accent text-xs px-3 py-1.5 font-medium">Add</span>
        <span className="rounded-full bg-accent-soft text-accent-text text-xs px-3 py-1.5">Pull forward</span>
      </div>
    </div>
  )
}
